import { CopyOutlined } from '@ant-design/icons';
import { Modal, Input, Button, Typography, Space, Tooltip } from 'antd';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';

const { Text } = Typography;

interface PublicToken {
  token?: string;
  [key: string]: any;
}

interface PublicTokenModalProps {
  deliveryId?: string;
  flowSlug?: string;
  handleCancel?: () => void;
  handleConfirm?: () => void;
  handlePublicTokenDownloadConfirm?: (url: string) => void;
  open?: boolean;
  publicTokens?: PublicToken[];
  selectedOrgSlug?: string;
  selectedProjectSlug?: string;
  urlType?: string;
}

function PublicTokenModal({
  deliveryId = '',
  flowSlug = '',
  handleCancel,
  handleConfirm,
  handlePublicTokenDownloadConfirm,
  open = false,
  publicTokens = [],
  selectedOrgSlug,
  selectedProjectSlug,
  urlType = 'orgDelivery',
}: PublicTokenModalProps): ReactElement {
  const [publicUrlCopied, updatePublicUrlCopied] = useState(false);
  const [defaultToken, setDefaultToken] = useState<PublicToken>({});

  useEffect(() => {
    if (publicTokens?.length) {
      setDefaultToken(publicTokens[0]);
    }
  }, [publicTokens]);

  useEffect(() => {
    if (!open) {
      updatePublicUrlCopied(false);
    }
  }, [open]);

  const handleOk = () => {
    if (handleConfirm) {
      handleConfirm();
    }
  };

  const publicTokenUrl = useMemo(() => {
    const publicOrgUrl = `${window.location.origin}/public/orgs/${selectedOrgSlug}`;
    const token = defaultToken?.token || '';
    switch (urlType) {
      case 'orgDelivery':
        return `${publicOrgUrl}/deliveries?token=${token}`;
      case 'flowDelivery':
        return `${publicOrgUrl}/projects/${selectedProjectSlug}/flows/${flowSlug}/deliveries?token=${token}`;
      case 'orgDeliveryDashboard':
        return `${publicOrgUrl}/deliveries/${deliveryId}/dashboard?token=${token}`;
      case 'flowDeliveryDashboard':
        return `${publicOrgUrl}/projects/${selectedProjectSlug}/flows/${flowSlug}/deliveries/${deliveryId}/dashboard?token=${token}`;
      case 'selectedDelivery':
        return `${publicOrgUrl}/deliveries/${deliveryId}?token=${token}`;
      case 'selectedFlowDelivery':
        return `${publicOrgUrl}/projects/${selectedProjectSlug}/flows/${flowSlug}/deliveries/${deliveryId}?token=${token}`;
      default:
        return flowSlug
          ? `${publicOrgUrl}/projects/${selectedProjectSlug}/flows/${flowSlug}/deliveries?token=${token}`
          : `${publicOrgUrl}/deliveries?token=${token}`;
    }
  }, [defaultToken, deliveryId, flowSlug, selectedOrgSlug, selectedProjectSlug, urlType]);

  const handleCopyPublicUrlClick = () => {
    if (handlePublicTokenDownloadConfirm) {
      handlePublicTokenDownloadConfirm(publicTokenUrl);
    }
    updatePublicUrlCopied(true);
  };

  return (
    <Modal
      title="Public Token"
      open={open}
      onCancel={handleCancel}
      onOk={handleOk}
      okText="OK"
      cancelText="Cancel"
      okButtonProps={{ disabled: !publicUrlCopied }}
      width={640}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space.Compact style={{ width: '100%' }}>
          <Input id="publicUrl" value={publicTokenUrl} disabled style={{ flex: 1 }} />
          <Tooltip title="Copy Public URL">
            <Button icon={<CopyOutlined />} onClick={handleCopyPublicUrlClick} />
          </Tooltip>
        </Space.Compact>
        <div style={{ textAlign: 'right', minHeight: 24 }}>
          <Text type="secondary">{publicUrlCopied ? 'Copied!' : 'Please copy the public URL above.'}</Text>
        </div>
      </Space>
    </Modal>
  );
}

export default PublicTokenModal;
